import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { postData } from '../../../actions/developer';
import { Button, Space, Tag, Modal, Input, message, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, EyeOutlined } from '@ant-design/icons';
import moment from 'moment';
import MasterComponent from '../../../components/Forms';
import { MasterConfigs } from '../../../interfaces/entity';
import ProfileCard from '../../../components/ContactInfo/ProfileCard';
import { constants } from '../../../constants/common';
import PdfView from '../../../components/custom/PDFView';

const { Text } = Typography;
const { TextArea } = Input;

interface PolicyApproval {
    _id: string;
    policyName: string;
    description: string;
    category: {
        _id: string;
        name: string;
    };
    addedBy: {
        _id: string;
        firstName: string;
        lastName: string;
        workEmail: string;
        avatar?: string;
        employeeId?: string;
    };
    policy?: string;
    createdAt: string;
    userApproval: {
        status: string;
        comments?: string;
    };
}

const PendingApprovals: React.FC = () => {
    const dispatch = useDispatch();
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [rejectComments, setRejectComments] = useState('');
    const [selectedPolicy, setSelectedPolicy] = useState<PolicyApproval | null>(null);
    const [pdfModalVisible, setPdfModalVisible] = useState(false);
    const [pdfUrl, setPdfUrl] = useState('');

    const handleApprove = async (policy: PolicyApproval) => {
        try {
            const resData = await dispatch(postData(`/policy/${policy._id}/approve`, {}));
            if (resData && resData.code === 200) {
                message.success('Policy approved successfully');
                // Trigger table refresh - ensure it toggles to trigger useEffect
                dispatch({ type: 'REFRESH_TABLE', refreshTable: false });
                setTimeout(() => {
                    dispatch({ type: 'REFRESH_TABLE', refreshTable: true });
                }, 10);
            } else {
                message.error(resData?.message || 'Failed to approve policy');
            }
        } catch (error) {
            console.error('Error approving policy:', error);
            message.error('Failed to approve policy');
        }
    };

    const handleRejectClick = (policy: PolicyApproval) => {
        setSelectedPolicy(policy);
        setRejectComments('');
        setRejectModalVisible(true);
    };

    const handleRejectConfirm = async () => {
        if (!selectedPolicy) return;

        try {
            const resData = await dispatch(postData(`/policy/${selectedPolicy._id}/reject`, {
                comments: rejectComments
            }));
            if (resData && resData.code === 200) {
                message.success('Policy rejected');
                setRejectModalVisible(false);
                setSelectedPolicy(null);
                setRejectComments('');
                // Trigger table refresh - ensure it toggles to trigger useEffect
                dispatch({ type: 'REFRESH_TABLE', refreshTable: false });
                setTimeout(() => {
                    dispatch({ type: 'REFRESH_TABLE', refreshTable: true });
                }, 10);
            } else {
                message.error(resData?.message || 'Failed to reject policy');
            }
        } catch (error) {
            console.error('Error rejecting policy:', error);
            message.error('Failed to reject policy');
        }
    };

    const handleViewPDF = (policy: PolicyApproval) => {
        if (policy.policy) {
            setPdfUrl(policy.policy);
            setPdfModalVisible(true);
        } else {
            message.warning('PDF not available for this policy');
        }
    };

    const getStatusTag = (status: string) => {
        switch (status) {
            case 'pending':
                return <Tag color="orange">Pending for approval</Tag>;
            case 'approved':
                return <Tag color="green">Approved</Tag>;
            case 'rejected':
                return <Tag color="red">Rejected</Tag>;
            default:
                return <Tag>Unknown</Tag>;
        }
    };

    const pendingApprovalsConfig: MasterConfigs = {
        api: () => '/policy/approvals/pending',
        title: 'Pending Policy Approvals',
        isActionTab: false, // We'll add custom actions in columns
        getColumns: () => {
            return [
                {
                    title: 'Policy Name',
                    dataIndex: 'policyName',
                    key: 'policyName',
                    width: 200,
                },
                {
                    title: 'Category',
                    dataIndex: 'category',
                    key: 'category',
                    width: 150,
                    render: (category) => category?.name || 'N/A',
                },
                {
                    title: 'Created By',
                    dataIndex: 'addedBy',
                    key: 'addedBy',
                    width: 200,
                    render: (addedBy) => (
                        <ProfileCard
                            size={30}
                            name={constants.GET_FULL_NAME(addedBy)}
                            avatarUrl={addedBy?.avatar ? process.env.FILE_URL + addedBy.avatar : ''}
                            role={addedBy?.employeeId}
                        />
                    ),
                },
                {
                    title: 'Status',
                    key: 'status',
                    width: 150,
                    render: (_, record: PolicyApproval) => getStatusTag(record.userApproval?.status || 'pending'),
                },
                {
                    title: 'Created At',
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    width: 180,
                    render: (createdAt) => moment(createdAt).format('DD MMM YYYY, hh:mm A'),
                },
                {
                    title: 'Actions',
                    key: 'actions',
                    width: 250,
                    fixed: 'right',
                    render: (_, record: PolicyApproval) => (
                        <Space size="small">
                            <Button
                                type="link"
                                icon={<EyeOutlined />}
                                onClick={() => handleViewPDF(record)}
                                disabled={!record.policy}
                                size="small"
                            >
                                View PDF
                            </Button>
                            <Button
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                onClick={() => handleApprove(record)}
                                size="small"
                            >
                                Approve
                            </Button>
                            <Button
                                danger
                                icon={<CloseCircleOutlined />}
                                onClick={() => handleRejectClick(record)}
                                size="small"
                            >
                                Reject
                            </Button>
                        </Space>
                    ),
                },
            ];
        },
        getData: () => ({}),
        tableProps: {
            scroll: { x: 1200 },
            pagination: {
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} policies`,
            },
        },
    };

    return (
        <>
            <div className="-mt-6">
                <MasterComponent config={pendingApprovalsConfig} />
            </div>

            <Modal
                title={`Reject Policy: ${selectedPolicy?.policyName}`}
                open={rejectModalVisible}
                onOk={handleRejectConfirm}
                onCancel={() => {
                    setRejectModalVisible(false);
                    setSelectedPolicy(null);
                    setRejectComments('');
                }}
                okText="Reject"
                okButtonProps={{ danger: true }}
            >
                <Text>Please provide a reason for rejecting this policy:</Text>
                <TextArea
                    rows={4}
                    value={rejectComments}
                    onChange={(e) => setRejectComments(e.target.value)}
                    placeholder="Enter rejection reason..."
                    style={{ marginTop: 16 }}
                />
            </Modal>

            <Modal
                title={selectedPolicy?.policyName || 'Policy PDF'}
                open={pdfModalVisible}
                onCancel={() => {
                    setPdfModalVisible(false);
                    setPdfUrl('');
                }}
                footer={null}
                width={1000}
            >
                {pdfUrl && <PdfView url={process.env.FILE_URL + pdfUrl} />}
            </Modal>
        </>
    );
};

export default PendingApprovals;
